import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";

const LazyLoadListWrapper = styled("div")<ILazyLoadListWrapperProps>`
	display: flex;
	flex-flow: ${props => props.direction};

	.skeleton {
		display: flex;
		flex-flow: ${props => props.direction};
	}
`;

interface ILazyLoadListWrapperProps {
	direction: "row" | "column";
}

interface LazyLoadProps {
	SkeletonComponent: FC;
	hasNextPage: boolean;
	loadNextPage: () => void;
	customStyles?: CSSProperties;
	skeletonNumber?: number;
	direction?: "row" | "column";
}

/**
 * ### Componente para lazy load de listas
 */
const LazyLoad: FC<LazyLoadProps> = ({ children, customStyles, SkeletonComponent, hasNextPage, loadNextPage, skeletonNumber = 1, direction }) => {
	const endRef = useRef<HTMLDivElement>(null);
	const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

	useEffect(() => {
		if (isIntersecting && hasNextPage) {
			loadNextPage();
		}
		return () => {
			setIsIntersecting(false);
		};
	}, [isIntersecting, hasNextPage]);

	const handleIsIntersecting = (entries: IntersectionObserverEntry[]) => {
		const [entry] = entries;
		setIsIntersecting(entry.isIntersecting);
	};

	useEffect(() => {
		const observer = new IntersectionObserver(handleIsIntersecting, {
			root: null,
			rootMargin: "0px",
			threshold: 0.2,
		});
		if (endRef.current) {
			//console.log('observe');
			observer.observe(endRef.current);
		}

		return () => {
			//console.log('unobserve');
			if (endRef.current) observer.unobserve(endRef.current);
		};
	}, [endRef.current]);

	const renderSkeleton = () => {
		const skeletonArray = [];
		for (let index = 0; index < (skeletonNumber || 1); index++) {
			skeletonArray.push(SkeletonComponent);
		}

		return skeletonArray.map((Skeleton, index) => <Skeleton key={index} />);
	};

	return (
		<LazyLoadListWrapper className="lazy-load-list-wrapper" direction={direction || "column"} style={{ ...customStyles }}>
			{children}
			{hasNextPage && (
				<div className="skeleton" ref={endRef}>
					{renderSkeleton()}
				</div>
			)}
		</LazyLoadListWrapper>
	);
};

export default LazyLoad;
